<script setup>
import { useI18n } from 'vue-i18n'

import { DROP_LIST_THEMES } from '@/utils/components-configurations/app-droplist'
import { FIELD_VARIANTS } from '@/utils/components-configurations/checkin-field'
import { CHECKIN_ENTITY_KEYS } from '@/utils/entity-keys'
import { includesValue } from '@/utils/prop-validators'

import { CHECKIN_FIELD_TEST_ID } from '@/components/check-ins/jest-helpers'
import TooltipButtonInfo from '@/components/TooltipButtonInfo'
import AppDivider from '@/components/ui/AppDivider'

defineOptions({
  name: 'CheckinField'
})

defineProps({
  field: {
    type: Object,
    required: true
  },

  variant: {
    type: String,
    default: FIELD_VARIANTS.ROW,
    validator: v => includesValue(v, FIELD_VARIANTS)
  },

  labelKey: {
    type: String,
    validator: v =>
      [
        CHECKIN_ENTITY_KEYS.LABEL,
        CHECKIN_ENTITY_KEYS.VIEW_LABEL,
        CHECKIN_ENTITY_KEYS.CREATE_LABEL
      ].includes(v),
    default: CHECKIN_ENTITY_KEYS.VIEW_LABEL
  }
})

const emit = defineEmits({
  'on-field-click': null
})

const { t } = useI18n()
</script>

<template>
  <div
    :class="{
      [`cf-Field-${variant}`]: variant !== FIELD_VARIANTS.ROW
    }"
    :data-auto-testid="CHECKIN_FIELD_TEST_ID"
    :data-testid="CHECKIN_FIELD_TEST_ID"
    class="cf-Field"
    @click.stop="emit('on-field-click', field[CHECKIN_ENTITY_KEYS.ID])"
  >
    <slot :field="field">
      <div class="cf-Field_Header">
        <div class="cf-Field_Label">
          <span class="oboard-truncated-text">
            {{ t(field[labelKey]) }}
          </span>

          <TooltipButtonInfo
            v-if="field[CHECKIN_ENTITY_KEYS.TOOLTIP]"
            :content="field[CHECKIN_ENTITY_KEYS.TOOLTIP]"
            :theme="`${DROP_LIST_THEMES.COMMON_TOOLTIP_THEMES} ${DROP_LIST_THEMES.TEXT_CENTER}`"
            :translate-content="false"
            arrow
            height="24"
            placement="top"
            width="24"
          />
        </div>

        <div class="cf-Field_Value">
          <slot :field="field" name="field-value" />
        </div>
      </div>
    </slot>

    <template v-if="$slots['field-footer']">
      <AppDivider border-width="2px" class="cd-Field_Divider" no-margin />

      <div class="cf-Field_Footer">
        <slot :field="field" name="field-footer" />
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.cf-Field {
  box-sizing: border-box;
  --cf-border-color: #{$grey-3-next};
  border-radius: var(--field-border-radius, #{$border-radius-md-next});
  --border-size: 2px;
  min-height: 56px;
  padding: var(--field-padding, 10px 18px);
  border: var(--field-border, var(--border-size) solid var(--cf-border-color));
  display: flex;
  justify-content: center;
  gap: 16px;
  flex-direction: column;
  width: var(--checkin-field-width);

  &-column {
    --field-padding: 14px 18px;
  }
}

.cf-Field_Header {
  display: flex;
  gap: inherit;
  width: 100%;
  justify-content: space-between;

  &:not(.cf-Field-column &) {
    flex-direction: row;
    align-items: center;
  }

  .cf-Field-column & {
    flex-direction: column;
  }
}

.cf-Field_Label {
  color: $dark-1;
  font-size: $fs-14;
  line-height: 16px;
  font-weight: fw('semi-bold');
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 4px;
}

.cf-Field_Value {
  flex-shrink: 0;
}

.cd-Field_Divider {
  --divider-color: var(--border-color);
  --divider-border-radius: #{$border-radius-xs};
}
</style>
